import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
    Envelope, PaperPlaneTilt, Eye, Plus, Trash, ClockCounterClockwise,
    CheckCircle, WarningCircle, ArrowsClockwise,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/layout/PageHeader";
import EmptyState from "../components/EmptyState";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { cn as classJoin } from "../lib/utils";

export default function PipelineDigestSettings() {
    const { can } = useAuth();
    // Client-side guard — backend already enforces 403; this just prevents
    // the page shell from rendering for non-admins per the UX contract.
    if (!can("user:write")) return <Navigate to="/dashboard" replace />;

    return <PipelineDigestSettingsInner />;
}

function PipelineDigestSettingsInner() {
    const [recipients, setRecipients] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sending, setSending] = useState(false);
    const [sendingTestTo, setSendingTestTo] = useState(null);
    const [previewHtml, setPreviewHtml] = useState(null);
    const [newRow, setNewRow] = useState({ email: "", name: "" });

    const load = async () => {
        try {
            const [r, h] = await Promise.all([
                api.get("/billing/reports/pipeline-digest/recipients"),
                api.get("/billing/reports/pipeline-digest/history", { params: { limit: 12 } }),
            ]);
            setRecipients(r.data.recipients || []);
            setHistory(h.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load digest settings"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const save = async (next) => {
        setSaving(true);
        try {
            const { data } = await api.put("/billing/reports/pipeline-digest/recipients", { recipients: next });
            setRecipients(data.recipients);
            toast.success("Recipients updated");
        } catch (err) {
            toast.error(formatApiError(err, "Failed to save"));
        } finally {
            setSaving(false);
        }
    };

    const addRecipient = async () => {
        const email = newRow.email.trim().toLowerCase();
        if (!email || !/.+@.+\..+/.test(email)) {
            toast.error("Enter a valid email");
            return;
        }
        if (recipients.some((r) => r.email === email)) {
            toast.error("Already in the list");
            return;
        }
        await save([...recipients, { email, name: newRow.name.trim() }]);
        setNewRow({ email: "", name: "" });
    };

    const removeRecipient = async (email) => {
        if (!window.confirm(`Remove ${email} from digest list?`)) return;
        await save(recipients.filter((r) => r.email !== email));
    };

    const previewDigest = async () => {
        try {
            const { data } = await api.get("/billing/reports/pipeline-digest/preview");
            setPreviewHtml(data);
        } catch (err) {
            toast.error(formatApiError(err, "Preview failed"));
        }
    };

    const sendNow = async () => {
        if (recipients.length === 0) {
            toast.error("No recipients configured");
            return;
        }
        if (!window.confirm(`Send digest now to ${recipients.length} recipient(s)? This bypasses the weekly cooldown.`)) return;
        setSending(true);
        try {
            const { data } = await api.post("/billing/reports/pipeline-digest/send-now");
            toast.success(`Digest sent to ${data.sent_to.length} recipients (${data.failures.length} failed)`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Send failed"));
        } finally {
            setSending(false);
        }
    };

    const sendTest = async (r) => {
        setSendingTestTo(r.email);
        try {
            const { data } = await api.post("/billing/reports/pipeline-digest/send-test", {
                email: r.email,
                name: r.name || "",
            });
            const modeNote = data.mode === "dry_run" ? " (dry-run — no Resend API key configured)" : "";
            toast.success(`Test digest sent to ${data.test_recipient}${modeNote}`);
        } catch (err) {
            toast.error(formatApiError(err, "Test send failed"));
        } finally {
            setSendingTestTo(null);
        }
    };

    const lastSent = history[0];

    return (
        <div data-testid="pipeline-digest-page">
            <PageHeader
                eyebrow="Settings"
                title={
                    <span className="flex items-center gap-3">
                        <Envelope size={22} weight="duotone" className="text-indigo-500" />
                        Pipeline Digest
                    </span>
                }
                description="Weekly snapshot to senior leadership. Auto-runs Monday 09:00 IST. Manage recipients and trigger ad-hoc sends here."
            />
            <div className="px-6 sm:px-8 py-8 space-y-6 max-w-5xl">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <StatusTile
                        icon={<Envelope size={14} className="text-indigo-600" />}
                        label="Recipients"
                        value={recipients.length}
                        sub={recipients.length === 0 ? "None — digest will not send" : "active"}
                    />
                    <StatusTile
                        icon={<ClockCounterClockwise size={14} className="text-slate-600" />}
                        label="Last sent"
                        value={lastSent ? formatDateTime(lastSent.sent_at) : "Never"}
                        sub={lastSent ? `${lastSent.recipients_count} recipients · ${lastSent.failures.length} failed` : "Add recipients to start"}
                    />
                    <StatusTile
                        icon={<ArrowsClockwise size={14} className="text-emerald-600" />}
                        label="Schedule"
                        value="Weekly"
                        sub="Monday 03:30 UTC (09:00 IST)"
                    />
                </div>

                {/* Recipients */}
                <div className="reedos-card">
                    <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                        <div>
                            <div className="reedos-eyebrow">Recipients</div>
                            <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">
                                {recipients.length} email{recipients.length !== 1 ? "s" : ""} on the digest list
                            </h3>
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={previewDigest} className="gap-1.5" data-testid="digest-preview-btn">
                                <Eye size={13} /> Preview
                            </Button>
                            <Button onClick={sendNow} disabled={sending || recipients.length === 0} className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5" data-testid="digest-send-now-btn">
                                <PaperPlaneTilt size={13} weight="fill" /> {sending ? "Sending…" : "Send now"}
                            </Button>
                        </div>
                    </div>
                    {recipients.length === 0 ? (
                        <div className="px-5 py-8 text-center" data-testid="digest-recipients-list" data-recipient-count="0">
                            <Envelope size={32} className="mx-auto text-slate-300" weight="thin" />
                            <p className="text-sm text-slate-500 mt-2">No recipients yet. Add at least one email below.</p>
                        </div>
                    ) : (
                        <ul className="divide-y divide-slate-100" data-testid="digest-recipients-list">
                            {recipients.map((r) => (
                                <li key={r.email} className="px-5 py-3 flex items-center gap-3" data-testid={`recipient-${r.email}`}>
                                    <div className="h-8 w-8 rounded-full bg-indigo-50 border border-indigo-200 flex items-center justify-center">
                                        <Envelope size={14} className="text-indigo-700" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-slate-900 text-sm truncate">{r.name || r.email}</div>
                                        <div className="text-[11px] text-slate-500 font-mono truncate">{r.email}</div>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => sendTest(r)}
                                        disabled={sendingTestTo === r.email}
                                        className="h-7 text-[11px] gap-1"
                                        data-testid={`recipient-test-${r.email}`}
                                        title="Send a one-off test digest to this recipient"
                                    >
                                        <PaperPlaneTilt size={11} weight="fill" />
                                        {sendingTestTo === r.email ? "Sending…" : "Test"}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => removeRecipient(r.email)}
                                        className="h-7 px-2 text-slate-400 hover:text-rose-600"
                                        data-testid={`recipient-remove-${r.email}`}
                                    >
                                        <Trash size={13} />
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    )}
                    {/* Add row */}
                    <div className="px-5 py-3 bg-slate-50/40 border-t border-slate-100">
                        <div className="grid grid-cols-1 sm:grid-cols-[1fr,1fr,auto] gap-2.5">
                            <div>
                                <Label className="text-[10px] font-semibold text-slate-500 uppercase">Email</Label>
                                <Input
                                    type="email"
                                    value={newRow.email}
                                    onChange={(e) => setNewRow({ ...newRow, email: e.target.value })}
                                    placeholder="ceo@company.com"
                                    className="mt-1 h-9 text-sm"
                                    data-testid="recipient-email-input"
                                />
                            </div>
                            <div>
                                <Label className="text-[10px] font-semibold text-slate-500 uppercase">Name (optional)</Label>
                                <Input
                                    value={newRow.name}
                                    onChange={(e) => setNewRow({ ...newRow, name: e.target.value })}
                                    placeholder="CEO"
                                    className="mt-1 h-9 text-sm"
                                    data-testid="recipient-name-input"
                                />
                            </div>
                            <div className="flex items-end">
                                <Button onClick={addRecipient} disabled={saving} className="h-9 bg-slate-900 hover:bg-slate-800 text-white gap-1.5" data-testid="recipient-add-btn">
                                    <Plus size={13} weight="bold" /> Add
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* History */}
                <div className="reedos-card">
                    <div className="px-5 py-4 border-b border-slate-200">
                        <div className="reedos-eyebrow">Send history</div>
                        <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">Last {history.length} digests</h3>
                    </div>
                    {history.length === 0 ? (
                        <EmptyState icon={<ClockCounterClockwise size={32} weight="thin" />} title="Nothing sent yet" />
                    ) : (
                        <table className="w-full text-sm" data-testid="digest-history-table">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr className="text-left">
                                    <th className="px-5 py-2 reedos-eyebrow">Sent at</th>
                                    <th className="px-5 py-2 reedos-eyebrow">Subject</th>
                                    <th className="px-5 py-2 reedos-eyebrow text-right">Recipients</th>
                                    <th className="px-5 py-2 reedos-eyebrow text-right">Failed</th>
                                    <th className="px-5 py-2 reedos-eyebrow">Trigger</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((d) => (
                                    <tr key={d.id} className="border-t border-slate-100">
                                        <td className="px-5 py-2 text-xs text-slate-700">{formatDateTime(d.sent_at)}</td>
                                        <td className="px-5 py-2 text-xs text-slate-900 truncate max-w-md">{d.subject}</td>
                                        <td className="px-5 py-2 text-xs text-right reedos-data">{d.recipients_count}</td>
                                        <td className="px-5 py-2 text-xs text-right reedos-data">
                                            {d.failures.length === 0 ? (
                                                <span className="inline-flex items-center gap-0.5 text-emerald-700"><CheckCircle size={11} weight="fill" /> 0</span>
                                            ) : (
                                                <span className="inline-flex items-center gap-0.5 text-rose-700"><WarningCircle size={11} weight="fill" /> {d.failures.length}</span>
                                            )}
                                        </td>
                                        <td className="px-5 py-2 text-xs">
                                            <span className={classJoin("inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold border",
                                                d.trigger === "manual" ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-slate-50 text-slate-700 border-slate-300")}>
                                                {d.trigger}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Preview dialog */}
            <Dialog open={!!previewHtml} onOpenChange={(v) => { if (!v) setPreviewHtml(null); }}>
                <DialogContent className="max-w-4xl">
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2">
                            <Eye size={18} className="text-slate-700" /> Digest preview
                        </DialogTitle>
                        <DialogDescription>{previewHtml?.subject}</DialogDescription>
                    </DialogHeader>
                    <div className="rounded-md border border-slate-200 overflow-hidden bg-slate-50" data-testid="digest-preview-iframe-wrap">
                        <iframe
                            srcDoc={previewHtml?.html || ""}
                            title="Digest preview"
                            className="w-full"
                            style={{ height: "70vh", border: 0 }}
                            sandbox=""
                        />
                    </div>
                </DialogContent>
            </Dialog>

            {loading && <div className="px-6 py-8 text-xs text-slate-400">Loading…</div>}
        </div>
    );
}

function StatusTile({ icon, label, value, sub }) {
    return (
        <div className="reedos-card p-4">
            <div className="reedos-eyebrow flex items-center gap-1.5">{icon} {label}</div>
            <div className="font-display text-xl font-bold text-slate-900 mt-1">{value}</div>
            {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
        </div>
    );
}
